import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getStored, setStored } from './utils';

// Фильтры страницы в URL (?q=&tag=...).
// spec: { filterKey: { param, type: 'str' | 'int' | 'bool' | 'range', empty } }
//   type 'range' — массив [beg, end] строк yyyy-MM-dd, в URL два параметра `${param}1`, `${param}2`
// Возвращает [filters, setFilter(patch)]
export function useUrlFilters(spec, storageKey) {
    const [searchParams, setSearchParams] = useSearchParams();

    const filters = useMemo(() => {
        const result = {};
        const stored = storageKey && !searchParams.toString() ? getStored(storageKey, {}) : {};
        Object.entries(spec).forEach(([key, s]) => {
            const empty = s.empty ?? null;
            if (stored && Object.prototype.hasOwnProperty.call(stored, key)) {
                result[key] = stored[key] ?? empty;
                return;
            }
            if (s.type === 'range') {
                const a = searchParams.get(`${s.param}1`);
                const b = searchParams.get(`${s.param}2`);
                result[key] = a || b ? [a, b] : empty;
            } else if (s.type === 'int') {
                result[key] = searchParams.has(s.param) ? Number(searchParams.get(s.param)) : empty;
            } else if (s.type === 'bool') {
                result[key] = searchParams.has(s.param) ? searchParams.get(s.param) === '1' : empty;
            } else {
                result[key] = searchParams.get(s.param) ?? empty;
            }
        });
        return result;
    }, [searchParams, spec, storageKey]);

    const toParams = useCallback((next, params) => {
        const qs = new URLSearchParams(params);
        Object.entries(spec).forEach(([key, s]) => {
            const empty = s.empty ?? null;
            const v = next[key];
            if (s.type === 'range') {
                qs.delete(`${s.param}1`);
                qs.delete(`${s.param}2`);
                if (Array.isArray(v) && (v[0] || v[1])) {
                    if (v[0]) qs.set(`${s.param}1`, v[0]);
                    if (v[1]) qs.set(`${s.param}2`, v[1]);
                }
            } else if (v === empty || v === '' || v == null) {
                qs.delete(s.param);
            } else {
                qs.set(s.param, s.type === 'bool' ? (v ? '1' : '0') : String(v));
            }
        });
        return qs;
    }, [spec]);

    useEffect(() => {
        if (searchParams.toString() || !storageKey) return;
        const qs = toParams(filters, searchParams);
        if (qs.toString()) setSearchParams(qs, { replace: true });
    }, [filters, searchParams, setSearchParams, storageKey, toParams]);

    const setFilter = useCallback((patch) => {
        const next = { ...filters, ...patch };
        if (storageKey) setStored(storageKey, next);
        setSearchParams(toParams(next, searchParams), { replace: true });
    }, [filters, searchParams, setSearchParams, storageKey, toParams]);

    return [filters, setFilter];
}
